import { ReactElement, useState, useCallback, useMemo } from 'react';
import { Stack } from '@mui/material';
import { useSnackbar } from 'notistack';

import { Nullable } from '~globals/types/commons';
import { useAppSelector } from '~hooks/useAppSelector';
import { useRequest } from '~hooks/useRequest';
import { selectAuth } from '~store/slices/auth';
import { getAdminCompany } from '~services/admin/company';
import { AdminCompany } from '~services/admin/company/types';
import { getAdminCompanySetting } from '~services/admin/companySetting';
import { AdminCompanySetting } from '~services/admin/companySetting/types';
import LoadingFallback from '~components/LoadingFallback';

import DialogConfigAndPreferencesSectionGeneral from './General';
import DialogConfigAndPreferencesSectionPreferences from './Preferences';
import DialogConfigAndPreferencesSectionDangerZone from './DangerZone';

const DialogConfigAndPreferencesSections = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentLoggedUser } = useAppSelector(selectAuth);

  const [companyData, setCompanyData] = useState<Nullable<AdminCompany>>(null);
  const [loadingCompanyData, setLoadingCompanyData] = useState(true);
  const [, , , executeGetCompanyData] = useRequest({
    request: getAdminCompany,
    payload: null,
    withPostSuccess: (response) => {
      const companyResponse = response.data?.data as AdminCompany;

      setCompanyData(companyResponse);

      setLoadingCompanyData(false);
    },
    withPostFailure: () => {
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });

      setLoadingCompanyData(false);
    },
  });
  const reloadCompanyData = useCallback(async () => {
    await executeGetCompanyData(null);
  }, [executeGetCompanyData]);

  const [preferencesData, setPreferencesData] =
    useState<Nullable<AdminCompanySetting>>(null);
  const [loadingPreferencesData, setLoadingPreferencesData] = useState(true);
  const [, , , executeGetPreferencesData] = useRequest({
    request: getAdminCompanySetting,
    payload: null,
    withPostSuccess: (response) => {
      const userResponse = response.data?.data as AdminCompanySetting;

      setPreferencesData(userResponse);

      setLoadingPreferencesData(false);
    },
    withPostFailure: () => {
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });

      setLoadingPreferencesData(false);
    },
  });
  const reloadPreferencesData = useCallback(async () => {
    await executeGetPreferencesData(null);
  }, [executeGetPreferencesData]);

  const loading = useMemo(
    () => loadingCompanyData || loadingPreferencesData,
    [loadingCompanyData, loadingPreferencesData],
  );

  if (loading) return <LoadingFallback />;

  return (
    <Stack spacing={2}>
      <DialogConfigAndPreferencesSectionGeneral
        data={companyData}
        reload={reloadCompanyData}
      />

      <DialogConfigAndPreferencesSectionPreferences
        data={preferencesData}
        reload={reloadPreferencesData}
      />

      {currentLoggedUser?.isOwner && (
        <DialogConfigAndPreferencesSectionDangerZone />
      )}
    </Stack>
  );
};

export default DialogConfigAndPreferencesSections;
